// useDashboardData.ts
// @ts-nocheck
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx-js-style";
import axiosInstance from "../../../../api/axios";
import { BASE_URL } from "../../../../constants/apiConstants";
import { RootState } from "../../../../store/store";
import { DaypartDTO, ProcessedDaypart } from "../../../../types/adminInterface";
import { applyXlsxSheetStyles, flattenDaypart, getFormattedDateRange, transformData } from "../../../../utils/helper";
import { headerStyle, contentStyle, sectionStyle, daypartStyle, subContentStyle, categoryStyle, titleStyle } from '../../../../utils/constants';


/** Example interface for your dropdown option shape. */
export interface Option {
  value: string;
  label: string;
  class?: string;
}

/** The return shape of your custom hook. */
interface UseDashboardDataReturn {
  loading: boolean;
  precomputedTableRows: ProcessedDaypart[];
  openIndices: number[];
  setOpenIndices: React.Dispatch<React.SetStateAction<number[]>>;
  plandatesArray: string[];
  handleTabFiltersChange: (key: string, value: any, tabIdx: number) => void;
  handleUMExport: (row: any) => Promise<void>;
  handleClientExport: (row: any) => Promise<void>;

}

export function useDashboardData(searchTerm: string): UseDashboardDataReturn {
  const userId = localStorage.getItem("userid");
  const location = useLocation();

  // Typically you have two slices in Redux
  const reduxParams = useSelector((state: RootState) =>
    location.pathname === "/sitemanagerdashboard" ? state.siteParams : state.kitchenParams
  );
  const params = reduxParams?.params;

  // Local state
  const [loading, setLoading] = useState(false);
  const [dayparts, setDayparts] = useState<ProcessedDaypart[]>([]);
  const [filteredData, setFilteredData] = useState<ProcessedDaypart[]>([]);
  const [manualOpenIndices, setManualOpenIndices] = useState<number[]>([]);
  const [plandatesArray, setPlandatesArray] = useState<string[]>([]);
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false);
  const { sites,cafes } = useSelector((state: any) => state.site);

  // ---- Helper: build "None" + mapped status objects
  const createDropdownStatusOptions = useCallback((statuses: string[]): Option[] => {
    const mapped = statuses.map((val) => ({ value: val, label: val, class: val }));
    // Prepend "None"
    return [{ value: "None", label: "None", class: "None" }, ...mapped];
  }, []);

  // ---- 1) fetchData
  const fetchData = useCallback(async () => {
    if (!params) {
      setDayparts([]);
      return;
    }
    setLoading(true);
    try {
      //Call API
      const response = await axiosInstance.post(
        location.pathname === "/sitemanagerdashboard"
          ? `${BASE_URL}/api/v1/InitiateMenuPlan/GetSiteManagerDashboard`
          : `${BASE_URL}/api/v1/InitiateMenuPlan/GetKitchenDashboard`,
        params
      );

      //   daypart array from server
      const rawDayparts: DaypartDTO[] = response?.data?.data?.daypart ?? [];


      // Build date range
      const fromDateObj = new Date(params.fromdate);
      const toDateObj = new Date(params.todate);

      const daypartsArr = rawDayparts.map((dp: DaypartDTO) => {
        const { originalRows, dateHeadings } = flattenDaypart(dp, fromDateObj, toDateObj);

        // Build local dropdown arrays
        const siteOptions = ["None", ...new Set(originalRows.map((r) => r.sitename))];
        const cafeOptions = ["None", ...new Set(originalRows.map((r) => r.cafename))];

        const menustatusOptionsBase = Array.from(
          new Set(originalRows.flatMap((r) => r.menustatus.map((ms) => ms.status)))
        ).filter(Boolean); // Remove empty or undefined values

        // For "initiatedby"
        const initiatedbyVals = Array.from(new Set(originalRows.map((r) => r.initiatedby))).filter(
          Boolean
        ) as string[];

        const initiatedbyOptions = createDropdownStatusOptions(initiatedbyVals);
        const menustatusOptions = createDropdownStatusOptions(menustatusOptionsBase);

        const initiatedOnOptions = [
          "None",
          ...new Set(originalRows.map((r) => r.initiatedon).filter(Boolean)),
        ];
        const submittedOnOptions = [
          "None",
          ...new Set(originalRows.map((r) => r.lastmodifiedon).filter(Boolean)),
        ];

        return {
          daypartcode: dp.daypartcode,
          name: dp.name,
          originalRows,
          filters: {},
          plandatesArray: dateHeadings,
          dropdownOptions: {
            siteOptions,
            cafeOptions,
            initiatedbyOptions,
            initiatedOnOptions,
            submittedOnOptions,
            menustatusOptions,
          },
        };
      });
      setDayparts(daypartsArr);
      setLoading(false);
    } catch (err) {
      console.error("fetchData => error:", err);
      setDayparts([]);
      setLoading(false);
    }
  }, [params, location.pathname, createDropdownStatusOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---- 2) applyAllFilters => merges local filter + global search
  const applyAllFilters = useCallback(() => {
    if (!dayparts.length) {
      setFilteredData([]);
      return;
    }

    const normSearch = searchTerm.trim().toLowerCase();
    const updated = dayparts.map((dp) => {
      const newRows = dp.originalRows.filter((row) => {
        const passFilters = Object.entries(dp.filters || {}).every(([fKey, fVal]) => {
          if (!fVal || fVal === "None") {
            return true;
          }
          if (fKey.startsWith("status-")) {
            const dateKey = fKey.replace("status-", "").toLowerCase();
            const passStatus = row.menustatus.some(
              (ms) =>
                (ms.formattedplandate || "").toLowerCase() === dateKey &&
                (ms.status || "").toLowerCase() === String(fVal).toLowerCase()
            );
            return passStatus;
          }

          if (fKey === "initiatedon") {
            const passInitiated =
              row.initiatedon.toLowerCase() === String(fVal).toLowerCase();
            return passInitiated;
          }

          if (fKey === "lastmodifiedon") {
            const passLastMod =
              row.lastmodifiedon.toLowerCase() === String(fVal).toLowerCase();
            return passLastMod;
          }

          const rowVal = (row as any)[fKey]?.toLowerCase() ?? "";
          const passDimension = rowVal === String(fVal).toLowerCase();
          return passDimension;
        });

        if (!passFilters) {
          return false; // Exclude row
        }

        if (normSearch.length > 0) {
          const passSearch =
            row.sitename.toLowerCase().includes(normSearch) ||
            row.cafename.toLowerCase().includes(normSearch) ||
            row.statuses.some((s) => s.toLowerCase().includes(normSearch));

          if (!passSearch) {
          }
          return passSearch;
        }

        return true;
      });
      return { ...dp, rows: newRows };
    });

    const finalDayparts = updated.filter((x) => x.rows && x.rows.length > 0);
    setFilteredData(finalDayparts);

    if (!hasLoadedInitially && finalDayparts.length && !normSearch) {
      setManualOpenIndices([0]);
      setHasLoadedInitially(true);
    }
    if (normSearch) {
      setManualOpenIndices(finalDayparts.map((_, i) => i));
    }
    if (finalDayparts.length) {
      setPlandatesArray(finalDayparts[0].plandatesArray);
    } else {
      setPlandatesArray([]);
    }
  }, [dayparts, searchTerm, hasLoadedInitially]);


  useEffect(() => {
    applyAllFilters();
  }, [dayparts, searchTerm, applyAllFilters]);

  const handleTabFiltersChange = useCallback(
    (key: string, value: any, tabIdx: number) => {
      setDayparts((prev) =>
        prev.map((dp, idx) => {
          if (idx !== tabIdx) return dp;
          const newFilters = { ...dp.filters };
          const isNone =
            !value ||
            value === "None" ||
            (Array.isArray(value) && value.includes("None"));
          if (isNone) {
            delete newFilters[key];
          } else {
            newFilters[key] = value;
          }
          return { ...dp, filters: newFilters };
        })
      );
    },
    []
  );

  const openIndices = useMemo(() => {
    const norm = searchTerm.trim().toLowerCase();
    if (norm.length > 0) {
      return filteredData.map((_, i) => i);
    }
    return manualOpenIndices;
  }, [searchTerm, filteredData, manualOpenIndices]);

  function getQtyAndPaxArray(data: any) {
    return data?.cafelist?.flatMap((cafe: any) => [
      cafe.pax === 0 ? "" : cafe.pax,
      cafe.requestedquantity === 0 ? "" : cafe.requestedquantity
    ])
  }

  const getReportData = (plandateData: any, mergedCellsRow, mergedRowIndex: any, rowIndexes, startingIndex) => {
    let excelData = [
      ...[['']],
    ]
    startingIndex++;
    mergedRowIndex += 1
    plandateData?.daypartmenulist.map((daypart: any) => {
      const cafesHeader = plandateData?.availablecafelist?.map((cafe: any) => cafe?.cafename)
      const columnHeaders = [["Item/Dish", "Total Quantity", "UOM", ...cafesHeader?.flat()?.flatMap((item: any) => [item, ""]), "Remarks"]]
      const maxWidth = columnHeaders[0]?.length
      excelData = [
        ...excelData,
        ...[[`${daypart?.name}`, ...Array(maxWidth - 1).fill('')]],
      ]
      rowIndexes.push({
        index: startingIndex,
        style: 'daypart'
      })
      mergedRowIndex += 1
      startingIndex++;
      rowIndexes.push({
        index: startingIndex,
        style: 'header'
      })
      startingIndex++;
      mergedRowIndex += 1
      const columnSubheader = [["", "", "", ...Array.from({ length: cafesHeader?.length }, () => ["Pax", "Requested Quantity"]).flat(), ""]]
      rowIndexes.push({
        index: startingIndex,
        style: 'header'
      })
      startingIndex++;
      excelData = [
        ...excelData,
        ...columnHeaders,
        ...columnSubheader
      ]
      mergedCellsRow.push(mergedRowIndex)
      mergedRowIndex += 1
      daypart?.menuplantypelist?.forEach((menuPlan: any) => {
        if (menuPlan?.menuplanname === "Food Program") {
          if (menuPlan?.foodprogramlist && menuPlan?.foodprogramlist?.length > 0) {
            excelData = [
              ...excelData,
              ...[[`${menuPlan?.menuplanname} Menu`, ...Array(maxWidth - 1).fill('')]]
            ]
            rowIndexes.push({
              index: startingIndex,
              style: 'content'
            })
            startingIndex++;
            mergedRowIndex += 1
            menuPlan?.foodprogramlist.forEach((list: any) => {
              if (list?.foodprogramname) {
                excelData = [
                  ...excelData,
                  ...[[`${list?.foodprogramname}`, ...Array(maxWidth - 1).fill('')]]
                ]
                rowIndexes.push({
                  index: startingIndex,
                  style: 'category'
                })
                startingIndex++;
                mergedRowIndex += 1
              }
              list?.dishcategories?.map((cat) => {
                if (cat?.dishcategoryname) {
                  excelData = [
                    ...excelData,
                    ...[[`${cat?.dishcategoryname}`, ...Array(maxWidth - 1).fill('')]]
                  ]
                  rowIndexes.push({
                    index: startingIndex,
                    style: 'content'
                  })
                  startingIndex++;
                  mergedRowIndex += 1
                  cat?.dishes?.map((dish) => {
                    dish?.portionvariants?.map((variant: any) => {
                      const dishesRow = [[`${variant?.portionvariantname}${variant?.weightperpc === 0 ? '' : (`@${variant?.weightperpc}gms`)}`, variant?.totalquantity, variant?.portionuomcode === 'Pcs' ? 'Pcs' : 'Kg', ...getQtyAndPaxArray(variant).flat(), variant?.remarks]]
                      excelData = [
                        ...excelData,
                        ...dishesRow
                      ]
                      startingIndex++;
                    })
                    mergedRowIndex = mergedRowIndex + dish?.portionvariants?.length
                  })
                } else {
                  cat?.items?.map((item) => {
                    item?.dishes?.map((dish) => {
                      dish?.portionvariants?.map((variant: any) => {
                        const dishesRow = [[`${variant?.portionvariantname}${variant?.weightperpc === 0 ? '' : (`@${variant?.weightperpc}gms`)}`, variant?.totalquantity, variant?.portionuomcode === 'Pcs' ? 'Pcs' : 'Kg', ...getQtyAndPaxArray(variant).flat(), variant?.remarks]]
                        excelData = [
                          ...excelData,
                          ...dishesRow
                        ]
                        startingIndex++;
                      })
                      mergedRowIndex = mergedRowIndex + dish?.portionvariants?.length
                    })
                  })
                }
                excelData.push([['']])
                startingIndex++;
                mergedRowIndex++;
              })

            })
          }
        } else if (menuPlan?.menuplanname === "Combo") {
          if (menuPlan?.combolist && menuPlan?.combolist?.length > 0) {
            excelData = [
              ...excelData,
              ...[[`${menuPlan?.menuplanname} Menu`, ...Array(maxWidth - 1).fill('')]]
            ]
            rowIndexes.push({
              index: startingIndex,
              style: 'content'
            })
            startingIndex++;
            mergedRowIndex += 1;
            menuPlan?.combolist.forEach((combo: any, i: any) => {              
               // Display combo name with custom combo name if available
              let comboDisplayName =  `${combo.comboname}: ${combo.customcomboname}`;        
              excelData = [
                ...excelData,
                ...[[comboDisplayName, ...Array(maxWidth - 1).fill('')]]
              ]
              rowIndexes.push({
                index: startingIndex,
                style: 'category'
              })
              startingIndex++;
              mergedRowIndex += 1;
              combo?.dishes?.map((dish) => {
                dish?.portionvariants?.map((variant: any) => {
                  const dishesRow = [[`${variant?.portionvariantname}${variant?.weightperpc === 0 ? '' : (`@${variant?.weightperpc}gms`)}`, variant?.totalquantity, variant?.portionuomcode === 'Pcs' ? 'Pcs' : 'Kg', ...getQtyAndPaxArray(variant).flat(), variant?.remarks]]
                  excelData = [
                    ...excelData,
                    ...dishesRow
                  ]
                  startingIndex++;
                })

                mergedRowIndex = mergedRowIndex + dish?.portionvariants?.length
              })
              excelData.push([['']])
              startingIndex++;
              mergedRowIndex++;
            })
          }
        } else {
          if (menuPlan?.dishcategories && menuPlan?.dishcategories?.length > 0) {
            excelData = [
              ...excelData,
              ...[[`${menuPlan?.menuplanname} Menu`, ...Array(maxWidth - 1).fill('')]]
            ]
            rowIndexes.push({
              index: startingIndex,
              style: 'content'
            })
            startingIndex++;
            mergedRowIndex += 1;
            menuPlan?.dishcategories.forEach((cat: any) => {
              if (cat?.dishcategoryname) {
                excelData = [
                  ...excelData,
                  ...[[`${cat?.dishcategoryname}`, ...Array(maxWidth - 1).fill('')]]
                ]
                rowIndexes.push({
                  index: startingIndex,
                  style: 'category'
                })
                startingIndex++;
                mergedRowIndex += 1;
              }
              cat?.dishes?.map((dish) => {
                dish?.portionvariants?.map((variant: any) => {
                  const dishesRow = [[`${variant?.portionvariantname}${variant?.weightperpc === 0 ? '' : (`@${variant?.weightperpc}gms`)}`, variant?.totalquantity, variant?.portionuomcode === 'Pcs' ? 'Pcs' : 'Kg', ...getQtyAndPaxArray(variant).flat(), variant?.remarks]]
                  excelData = [
                    ...excelData,
                    ...dishesRow
                  ]
                  startingIndex++;
                })
                mergedRowIndex = mergedRowIndex + dish?.portionvariants?.length
              })
              excelData.push([['']])
              startingIndex++;
              mergedRowIndex++;
            })

          }
        }
        excelData = [
          ...excelData,
          //...[[]]
        ]
        // startingIndex++;
        // mergedRowIndex += 1;
      })
    })
    return excelData;
  }

  const handleUMExport = useCallback(
    async (row: any) => {
      try {
        setLoading(true);
        const payload = {
          userid: userId,
          fromdate: params?.fromdate,
          todate: params?.todate,
          sitecode: String(row?.sitecode || ""),
        };

        await axiosInstance.post(`${BASE_URL}/api/v1/MenuRequisition/GetSiteManageMenuRequisitionReport`, payload)
          .then((res: any) => {
            setLoading(false);
            if (res && res?.data?.data?.plandatedatalist?.length > 0) {
              const UMExportData = res?.data
              const workbook = XLSX.utils.book_new();
              UMExportData?.data?.plandatedatalist?.map((plandate: any) => {
                let mergedCellsRow = []
                let mergedRowIndex = 0
                let startingIndex = 0
                let rowIndexes = [{
                  index: startingIndex,
                  style: 'title'
                }]
                startingIndex++;
                const header = [[`Menu Requisition for ${UMExportData?.data?.sitename}`]]
                const dateHeader = [[plandate?.deliverydate ? `Date: ${format(new Date(plandate?.deliverydate), 'dd MMM yyyy')}` : 'No Date', "", "", "", `Revenue per day: ₹${plandate?.revenueperday}`]]
                let exportData: any = [
                  ...header,
                  ...dateHeader
                ];
                startingIndex++;
                mergedRowIndex += 1
                const combinedData = getReportData(plandate, mergedCellsRow, mergedRowIndex, rowIndexes, startingIndex)
                exportData = [
                  ...exportData,
                  ...combinedData
                ]
                // Create a new workbook and worksheet
                const worksheet = XLSX.utils.aoa_to_sheet(exportData);
                const dataCols = Math.max(
                  8,
                  ...(Array.isArray(exportData) ? exportData.map((r: any) => (Array.isArray(r) ? r.length : 0)) : [8])
                );
                let mergedCells = [
                  { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(5, dataCols - 1) } }
                ]
                const numberOfCafes = (plandate?.availablecafelist?.length * 2) + 3
                const remarksColIndex = numberOfCafes;
                const columns = Array.from({ length: dataCols }, () => ({ wch: 14 }));
                columns[0] = { wch: 34 }; // Item/Dish
                if (columns[1]) columns[1] = { wch: 16 }; // Total Quantity
                if (columns[2]) columns[2] = { wch: 12 }; // UOM
                for (let i = 3; i < numberOfCafes; i += 2) {
                  if (columns[i]) columns[i] = { wch: 16 }; // Cafe - Pax
                  if (columns[i + 1]) columns[i + 1] = { wch: 20 }; // Cafe - Requested Quantity
                }
                if (columns[remarksColIndex]) columns[remarksColIndex] = { wch: 24 }; // Remarks
                worksheet['!cols'] = columns;
                mergedCellsRow.forEach(row => {
                  for (let i = 3; i < numberOfCafes; i += 2) {
                    mergedCells.push({ s: { r: row, c: i }, e: { r: row, c: i + 1 } })
                  }
                })
                worksheet["!merges"] = mergedCells
                rowIndexes?.forEach((rowIndex => {
                  // Apply the header style to all header cells
                  for (let colIndex = 0; colIndex < dataCols; colIndex++) {
                    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex?.index, c: colIndex });
                    if (!worksheet[cellAddress]) {
                      worksheet[cellAddress] = {};
                    }
                    switch (rowIndex?.style) {
                      case 'title':
                        worksheet[cellAddress].s = titleStyle;
                        break;
                      case 'header':
                        worksheet[cellAddress].s = headerStyle;
                        break;
                      case 'section':
                        worksheet[cellAddress].s = sectionStyle;
                        break;
                      case 'content':
                        worksheet[cellAddress].s = contentStyle;
                        break;
                      case 'daypart':
                        worksheet[cellAddress].s = daypartStyle;
                        break;
                      case 'subContent':
                        worksheet[cellAddress].s = subContentStyle;
                        break;
                      case 'category':
                        worksheet[cellAddress].s = categoryStyle;
                        break;
                      default:
                        break;
                    }
                  }
                }))
                applyXlsxSheetStyles(worksheet, rowIndexes, exportData.length, dataCols);

                // Alignment rule: only Item/Dish and Remarks left, all other headers/data centered
                for (let rowIndex = 2; rowIndex < exportData.length; rowIndex++) {
                  const rowData = Array.isArray(exportData[rowIndex]) ? exportData[rowIndex] : [];
                  const rowDataLength = rowData.length;
                  for (let colIndex = 0; colIndex < rowDataLength && colIndex < dataCols; colIndex++) {
                    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                    if (!worksheet[cellAddress]) continue;
                    if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};

                    const isItemOrRemarks = colIndex === 0 || colIndex === remarksColIndex;
                    worksheet[cellAddress].s = {
                      ...worksheet[cellAddress].s,
                      alignment: {
                        horizontal: isItemOrRemarks ? 'left' : 'center',
                        vertical: 'center'
                      }
                    };
                  }
                }

                XLSX.utils.book_append_sheet(workbook, worksheet, `${format(new Date(plandate?.deliverydate), 'dd MMM yyyy')}`);
              })
              // Export the workbook to an Excel file
              XLSX.writeFile(workbook, `Site_Manager_UM_Export-${new Date().toLocaleDateString('en-ZA')}.xlsx`);
              toast("File downloaded successfully!", { type: "success" });
            } else {
              toast("No data available to export", { type: "info" });
            }
          })
          .catch((err) => {
            setLoading(false);
            console.log(err)
            toast("Some Error occured while preparing the file", { type: "error" })
          })
      } catch (error) {
        setLoading(false);
        console.log(error)
        toast("Some Error occured while preparing the file", { type: "error" })
      }
    },
    [params, userId]
  );


  const getDataForExport = (data: any, row: any, rowIndexes: any) => {
    const days = getFormattedDateRange(params?.fromdate, params?.todate, 'EEE, dd-MMM-yy')
    if ((data?.length > 0) && days?.length > 0) {
      const titleRow = [[`Menu Plan from ${days[0]} to ${days[days?.length - 1]} for ${row?.sitename} - ${row?.cafename}`, ...Array(7).fill('')]]
      let startingIndex = 0
      rowIndexes.push({
        index: startingIndex,
        style: 'title'
      })
      let combinedData = [
        ...titleRow
      ]
      try {
        const rawData = transformData(data)
        rawData.forEach(plan => {
          rowIndexes.push({
            index: startingIndex + 1,
            style: 'daypart'
          })
          rowIndexes.push({
            index: startingIndex + 2,
            style: 'header'
          })
          startingIndex += 2;
          let datesHeader = [];
          let subheadingRow = [];
          let isMenuTypePresent = false
          const menuplantypes = Object.keys(plan).filter(key => key !== 'daypart')
          const db = []
          menuplantypes.forEach(key => {
            let isDishesPresent = false
            const categoryData = []
            categoryData.push([[plan[key]?.name], ...Array(7).fill('')])
            startingIndex++;
            rowIndexes.push({
              index: startingIndex,
              style: 'header'
            })
            if (plan[key]?.name === "Food Program") {
              plan[key]?.data.length && plan[key]?.data?.map(foodprogram => {
                if (!!foodprogram?.foodProgramName) {
                  categoryData.push([[foodprogram?.foodProgramName], ...Array(7).fill('')])
                  startingIndex++;
                  rowIndexes.push({
                    index: startingIndex,
                    style: 'section'
                  })
                }
                foodprogram?.categories?.map(cat => {
                  const maxDishes = Math.max(...cat?.dates.map(date => date?.items?.length));
                  if (cat?.category) {
                    categoryData.push([[cat?.category], ...Array(7).fill('')])
                    startingIndex++;
                    rowIndexes.push({
                      index: startingIndex,
                      style: 'category'
                    })
                  }
                  if (maxDishes) isDishesPresent = true, isMenuTypePresent = true;
                  for (let index = 0; index < maxDishes; index++) {
                    let row = [""]
                    cat?.dates?.map(day => {
                      row?.push(day?.items[index] ? `${day?.items[index]?.dishname}` : "")
                    })
                    categoryData.push(row)
                    startingIndex++;
                  }
                  categoryData.push([['']])
                  startingIndex++;
                })
              })
            } else if (plan[key]?.name === "Combo") {
              plan[key]?.data?.map((comboitem) => {
                const maxDishes = Math.max(...comboitem?.dates.map(date => date?.dishes?.length));
                if (maxDishes) {
                  // Display combo name with custom combo name if available
                  let comboDisplayName = `${comboitem.comboname}: ${comboitem.customcomboname}`;
                  categoryData.push([[comboDisplayName], ...Array(7).fill('')])
                  startingIndex++;
                  rowIndexes.push({
                    index: startingIndex,
                    style: 'category'
                  })
                }
                if (maxDishes) isDishesPresent = true, isMenuTypePresent = true;
                for (let index = 0; index < maxDishes; index++) {
                  let row = [""]
                  comboitem?.dates?.map(day => {
                    row?.push(day?.dishes[index] ? `${day?.dishes[index]?.dishname}` : "")
                  })
                  categoryData.push(row)
                  startingIndex++;
                }
                categoryData.push([['']])
                startingIndex++;
              })
            } else if (plan[key]?.name === "Regular" || plan[key]?.name === "Event" || plan[key]?.name === "Bakery") {
              plan[key]?.data?.map(menuPlan => {
                const maxDishes = Math.max(...menuPlan?.dates.map(date => date?.dishes?.length));
                if (maxDishes) isDishesPresent = true, isMenuTypePresent = true;
                if (maxDishes && !!menuPlan?.dishCategory) {
                  categoryData.push([[menuPlan?.dishCategory], ...Array(7).fill('')])
                  startingIndex++;
                  rowIndexes.push({
                    index: startingIndex,
                    style: 'category'
                  })
                }
                for (let index = 0; index < maxDishes; index++) {
                  let row = [""]
                  menuPlan?.dates?.map(day => {
                    row?.push(day?.dishes[index] ? `${day?.dishes[index]?.dishname}` : "")
                  })
                  categoryData.push(row)
                  startingIndex++;
                }
                categoryData.push([['']])
                startingIndex++;
              })
            }
            // uncomment for debugging
            // console.log(plan.daypart, plan[key]?.name, "isDishesPresent", isDishesPresent)
            if (isDishesPresent) {
              categoryData.map(cat => db.push(cat))
            }
          })

          if (isMenuTypePresent) {
            subheadingRow = [[plan?.daypart, ...Array(7).fill('')]]
            datesHeader = [['', ...days]]
            combinedData = [
              ...combinedData,
              ...subheadingRow,
              ...datesHeader,
              ...db,
              //...[[], []]
            ]
            //startingIndex += 2
          }
        });
      } catch (error) {
        console.log(error)
      }
      return combinedData
    }
    return []
  }

  /**
   * Export function for the item-wise API response.
   * Structure: daypartmenureportlist → plandatereportlist → menuplantypelist → items → dishcategories → dishes
   */
  const getDataForExportItemWise = (data: any, row: any, rowIndexes: any) => {
    const days = getFormattedDateRange(params?.fromdate, params?.todate, 'EEE, dd-MMM-yy');
    if (!data?.length || !days?.length) return [];

    const titleRow = [[`Menu Plan from ${days[0]} to ${days[days.length - 1]} for ${row?.sitename} - ${row?.cafename}`, ...Array(7).fill('')]];
    let startingIndex = 0;
    rowIndexes.push({ index: startingIndex, style: 'title' });
    let combinedData: any[] = [...titleRow];

    data.forEach((daypart: any) => {
      const plandates: any[] = daypart.plandatereportlist || [];
      if (!plandates.length) return;

      // Date labels for the header row (one column per plandate)
      const dateLabels = plandates.map((pd: any) =>
        pd.plandate ? format(new Date(pd.plandate), 'EEE, dd-MMM-yy') : ''
      );

      // Collect unique menuplantype names in encounter order
      const menuplantypeNames: string[] = [];
      plandates.forEach((pd: any) => {
        (pd.menuplantypelist || []).forEach((mpt: any) => {
          if (!menuplantypeNames.includes(mpt.menuplantypename)) {
            menuplantypeNames.push(mpt.menuplantypename);
          }
        });
      });

      let isAnyDataPresent = false;
      const db: any[] = [];
      let dbRowIndexes: any[] = [];
      let dbStartingIndex = startingIndex + 2; // will be offset after subheading + dates header rows

      menuplantypeNames.forEach(mptName => {
        // Collect unique item names in encounter order for this menuplantype
        const itemNames: string[] = [];
        plandates.forEach((pd: any) => {
          const mpt = (pd.menuplantypelist || []).find((m: any) => m.menuplantypename === mptName);
          (mpt?.items || []).forEach((item: any) => {
            if (!itemNames.includes(item.itemname)) itemNames.push(item.itemname);
          });
        });

        let isDishesPresent = false;
        const categoryRows: any[] = [];
        const categoryRowIndexes: any[] = [];
        let catIdx = dbStartingIndex + 1; // +1 for menuplantype header row

        itemNames.forEach(itemName => {
          // For each date, collect all dishes under this item
          const dishesPerDate = plandates.map((pd: any) => {
            const mpt = (pd.menuplantypelist || []).find((m: any) => m.menuplantypename === mptName);
            const item = (mpt?.items || []).find((it: any) => it.itemname === itemName);
            const dishes: string[] = [];
            (item?.dishcategories || []).forEach((dc: any) => {
              (dc.dishes || []).forEach((d: any) => dishes.push(d.dishname || ''));
            });
            return dishes;
          });

          const maxDishes = Math.max(...dishesPerDate.map((d: any[]) => d.length), 0);
          if (maxDishes === 0) return;

          isDishesPresent = true;

          // Item name — category style header
          categoryRows.push([[itemName], ...Array(7).fill('')]);
          categoryRowIndexes.push({ index: catIdx, style: 'category' });
          catIdx++;

          // One row per dish slot across all dates
          for (let i = 0; i < maxDishes; i++) {
            const dishRow: string[] = [''];
            dishesPerDate.forEach((dishes: string[]) => {
              dishRow.push(dishes[i] || '');
            });
            categoryRows.push(dishRow);
            catIdx++;
          }

          // Blank spacer row
          categoryRows.push(['']);
          catIdx++;
        });

        if (!isDishesPresent) return;

        isAnyDataPresent = true;

        // Menuplantype header row
        db.push([[mptName], ...Array(7).fill('')]);
        dbRowIndexes.push({ index: dbStartingIndex + 1, style: 'header' });

        categoryRows.forEach(r => db.push(r));
        categoryRowIndexes.forEach(ri => dbRowIndexes.push(ri));

        dbStartingIndex = catIdx;
      });

      if (isAnyDataPresent) {
        const subheadingRow = [[daypart.name, ...Array(7).fill('')]];
        const datesHeader = [['', ...dateLabels]];

        // Row indexes for subheading and datesHeader
        rowIndexes.push({ index: startingIndex + 1, style: 'daypart' });
        rowIndexes.push({ index: startingIndex + 2, style: 'header' });
        startingIndex += 2;

        // Offset dbRowIndexes by actual startingIndex
        dbRowIndexes.forEach(ri => {
          rowIndexes.push({ index: ri.index, style: ri.style });
        });
        startingIndex = dbStartingIndex;

        combinedData = [...combinedData, ...subheadingRow, ...datesHeader, ...db];
      }
    });

    return combinedData;
  };

  const handleClientExport = useCallback(
    async (row: any) => {
      try {
        setLoading(true);
        let userIsItem = false;
        try {
          const rawUser = localStorage.getItem('userInfo');
          if (rawUser) {
            const parsed = JSON.parse(rawUser);
            const val = parsed?.userdetails?.isitemwise;
            userIsItem = val === true;
          }
        } catch (e) {
          userIsItem = false;
        }
  const payload = {
          userid: userId,
          fromdate: params?.fromdate,
          todate: params?.todate,
          siteidlist: [String(row?.siteid || "")],
          isallsite: false,
          isallcafe: false,
          cafeidlist: row?.cafecode ? [String(row?.cafecode)] : [],
        };
        if(userIsItem)
        {
           const reportApi = 
          `${BASE_URL}/api/v1/ItemWisePlanning/InitiateMenuPlanItemWise/GetItemWiseInitiateMenuPlanReportPublished`
          // : `${BASE_URL}/api/v1/InitiateMenuPlan/GetInitiateMenuPlanReport`;

      

        await axiosInstance.post(reportApi, payload)
          .then((db) => {
            setLoading(false)
            const reportData = db?.data?.data?.daypartmenureportlist;
            let rowIndexes = []
            const data = getDataForExportItemWise(reportData, row, rowIndexes);
            // Create a new workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet(data);
            const dataCols = Math.max(
              8,
              ...(Array.isArray(data) ? data.map((r: any) => (Array.isArray(r) ? r.length : 0)) : [8])
            );
            const columns = Array.from({ length: dataCols }, (_, idx) => ({ wpx: idx === 0 ? 170 : 180 }));
            worksheet['!cols'] = columns;
            worksheet["!merges"] = [
              { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(3, dataCols - 1) } },
            ];
            rowIndexes?.forEach((rowIndex => {
              // Apply the header style to all header cells
              for (let colIndex = 0; colIndex < dataCols; colIndex++) {
                const cellAddress = XLSX.utils.encode_cell({ r: rowIndex?.index, c: colIndex });
                if (!worksheet[cellAddress]) {
                  worksheet[cellAddress] = {};
                }
                switch (rowIndex?.style) {
                  case 'title':
                    worksheet[cellAddress].s = titleStyle;
                    break;
                  case 'header':
                    worksheet[cellAddress].s = headerStyle;
                    break;
                  case 'section':
                    worksheet[cellAddress].s = sectionStyle;
                    break;
                  case 'content':
                    worksheet[cellAddress].s = contentStyle;
                    break;
                  case 'daypart':
                    worksheet[cellAddress].s = daypartStyle;
                    break;
                  case 'subContent':
                    worksheet[cellAddress].s = subContentStyle;
                    break;
                  case 'category':
                    worksheet[cellAddress].s = categoryStyle;
                    break;
                  default:
                    break;
                }
              }
            }))
            applyXlsxSheetStyles(worksheet, rowIndexes, data.length, dataCols);
            XLSX.utils.book_append_sheet(workbook, worksheet, row?.sitename.length > 31 ? row?.sitename.split('(')[0].slice(0, 31) : row?.sitename); // check for sheetname 31 char limit
            // Export the workbook to an Excel file
            XLSX.writeFile(workbook, `Site_Manager_Client_Export_${new Date().toLocaleDateString('en-ZA')}.xlsx`);
          })
          .catch((err) => {
            console.log(err)
            setLoading(false);
            toast("Some Error occured while preparing the file", { type: "error" })
          })

        }else{          
           const payload1 = {
          userid: userId,
          fromdate: params?.fromdate,
          todate: params?.todate,
          siteidlist: [String(row?.sitecode || "")],
          isallsite: false,
          isallcafe: false,
          cafeidlist: row?.cafecode ? [String(row?.cafecode)] : [],
        };
try {
      const db = await axiosInstance.post(`${BASE_URL}/api/v1/InitiateMenuPlan/GetInitiateMenuPlanReportnew`, payload1)
      setLoading(false)

      const workbook = XLSX.utils.book_new();

      db?.data?.data?.forEach((item: any) => {
        // Only export the "Overall" section — skip CPU and Site sheets
        if ((item?.plantypesection || '').toLowerCase() !== 'overall') return;

        const siteCafeDetails = item?.daypartmenureportlist || [];
      let rowIndexes: any[] = []

      // const siteItems = sites.filter(site => siteidlist.includes(site.siteid)).map(site => ({ id: site.siteid, code: site.sitecode, label: site.sitealias }));
      // const cafeItems = cafes.filter(cafe => cafeidlist.includes(cafe.id)).map(cafe => ({ id: cafe.id, code: cafe.cafecode, label: cafe.cafename }));
      const selectedSiteDetails = {
        siteName: row?.sitename,
       // siteCode: siteItems[0]?.code,
        cafeName: row?.cafename,
        cafeCode: row?.cafecode,
        // getDataForExport reads row?.sitename and row?.cafename (lowercase) — must match
        sitename: row?.sitename,
        cafename: row?.cafename,
      }

      const data = getDataForExport(siteCafeDetails, selectedSiteDetails, rowIndexes)
      const wids = [{ wpx: 160 }]
      
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      worksheet['!cols'] = [wids[0], ...Array(9).fill(wids[0])];
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
      ];
      rowIndexes?.forEach((rowIndex => {
        for (let colIndex = 0; colIndex < 8; colIndex++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIndex?.index, c: colIndex });
          if (!worksheet[cellAddress]) worksheet[cellAddress] = {};
          switch (rowIndex?.style) {
            case 'title': worksheet[cellAddress].s = titleStyle; break;
            case 'header': worksheet[cellAddress].s = headerStyle; break;
            case 'section': worksheet[cellAddress].s = sectionStyle; break;
            case 'content': worksheet[cellAddress].s = contentStyle; break;
            case 'daypart': worksheet[cellAddress].s = daypartStyle; break;
            case 'subContent': worksheet[cellAddress].s = subContentStyle; break;
            case 'category': worksheet[cellAddress].s = categoryStyle; break;
            default: break;
          }
        }
      }))
      applyXlsxSheetStyles(worksheet, rowIndexes, data.length, 8);
      // build a safe sheet name from the API's plantypesection value (e.g. "Overall")
      const sheetTitle = item?.plantypesection || 'Overall';
      // preserve parentheses and hyphens, collapse multiple spaces, then trim and slice to 31 chars
      const sheetName = (sheetTitle
        .replace(/[^a-zA-Z0-9 _()\-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 31)) || 'Overall';
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      })      
      XLSX.writeFile(workbook, `Menu_Projection_Client_Export_${new Date().toLocaleDateString('en-ZA')}.xlsx`);
    } catch (err) {
      console.log(err)
      setLoading(false)
      toast("Some Error occured while preparing the file", { type: "error" })
    }

        }
       
      } catch (error) {
        console.log(error)
        toast("Some Error occured while preparing the file", { type: "error" })
      }
    },
    [params, userId, plandatesArray]
  );

  return {
    loading,
    precomputedTableRows: filteredData, // dayparts with .rows
    openIndices,
    setOpenIndices: setManualOpenIndices,
    plandatesArray,
    handleTabFiltersChange,
    handleUMExport,
    handleClientExport,
  };
}
